/**
 * Phone formatting for US 10-digit numbers.
 *
 * `parents.phone` is matched **exactly** against the Zelle sender string in
 * `fetch-zelle-emails` (`.eq("phone", parsed.sender)`), so every write path has
 * to store the same canonical shape or a parent's Zelle payments stop
 * auto-matching. All 310 phones in prod are `(xxx) xxx-xxxx`; keep it that way.
 *
 * This lived as three identical copies in AdminParents, AdminTeachers and
 * ParentOnboarding before Account settings became a fourth write path.
 */

/** Canonical storage form: `(650) 555-0100`. Returns `raw` unchanged if it isn't 10 digits. */
export function formatPhoneCanonical(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits.length !== 10) return raw;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/** Progressive mask for a controlled input — formats as the user types, caps at 10 digits. */
export function handlePhoneInput(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits.length ? `(${digits}` : '';
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/** True when `value` is empty (clearing the field) or a complete 10-digit number. */
export function isValidPhoneOrEmpty(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return true;
  return trimmed.replace(/\D/g, '').length === 10;
}
